import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiService, { getStorageUrl } from '../../../../services/apiService';
import { useTenant } from '../../../../contexts/TenantContext';

const ThrowballNewsDetail = () => {
  const { siteSettings } = useTenant();
  const { id } = useParams();
  const [newsItem, setNewsItem] = useState(null);
  const [allNews, setAllNews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Static/Default news items - used as fallback
  // No fallback articles. These were the founding federation's own news and
  // showed on any tenant that had published none.
  const staticNews = [];

  useEffect(() => {
    fetchNewsData();
  }, [id]);

  const fetchNewsData = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/public/site-settings');
      const data = response.data;

      if (data.success && data.data && data.data.news_items && Array.isArray(data.data.news_items) && data.data.news_items.length > 0) {
        const newsItems = data.data.news_items.map((item, index) => ({
          id: item.id || String(index + 1),
          title: item.title || '',
          excerpt: item.excerpt || '',
          content: item.content || '',
          image: item.image ? getStorageUrl(item.image) : null,
          certificate: item.certificate || null,
          date: item.date || ''
        }));

        setAllNews(newsItems);
        const currentNews = newsItems.find(n => String(n.id) === String(id));
        setNewsItem(currentNews || null);
      } else {
        setAllNews(staticNews);
        const currentNews = staticNews.find(n => String(n.id) === String(id));
        setNewsItem(currentNews || null);
      }
    } catch (error) {
      console.error('Error fetching news data:', error);
      setAllNews(staticNews);
      const currentNews = staticNews.find(n => String(n.id) === String(id));
      setNewsItem(currentNews || null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get related news (excluding current)
  const relatedNews = allNews.filter(n => String(n.id) !== String(id)).slice(0, 3);

  // Default placeholder image
  const defaultImage = 'https://picsum.photos/id/1015/600/400';

  if (loading) {
    return (
      <section className="tb-page-section tb-news-detail-section">
        <div className="tb-container">
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <p>Loading...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!newsItem) {
    return (
      <section className="tb-page-section tb-news-detail-section">
        <div className="tb-container">
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <h2>News Article Not Found</h2>
            <p style={{ marginBottom: '20px' }}>The requested news article could not be found.</p>
            <Link to="/news" style={{
              display: 'inline-block',
              padding: '10px 20px',
              backgroundColor: '#e74c3c',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: '5px'
            }}>
              Back to News
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="tb-page-section tb-news-detail-section">
      <div className="tb-container">
        {/* Top Banner */}
        <div className="tb-page-banner">
          <img src={siteSettings?.page_banners?.news || '/theme/assets/images/banner-4.jpg'} alt="News Banner" />
        </div>

        {/* Page Content */}
        <div className="tb-page-content">
          {/* Breadcrumb */}
          <div style={{ marginBottom: '20px', fontSize: '14px', color: '#666' }}>
            <Link to="/" style={{ color: '#e74c3c', textDecoration: 'none' }}>Home</Link>
            <span style={{ margin: '0 10px' }}>/</span>
            <Link to="/news" style={{ color: '#e74c3c', textDecoration: 'none' }}>News</Link>
            <span style={{ margin: '0 10px' }}>/</span>
            <span>{newsItem.title}</span>
          </div>

          <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
            {/* Main Content */}
            <div style={{ flex: '2', minWidth: '300px' }}>
              {/* Title */}
              <h1 style={{
                fontSize: '32px',
                fontWeight: '700',
                color: '#333',
                marginBottom: '15px',
                lineHeight: '1.3'
              }}>{newsItem.title}</h1>

              {/* Date */}
              {newsItem.date && (
                <p style={{
                  color: '#999',
                  fontSize: '14px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="ri-calendar-line"></i>
                  {formatDate(newsItem.date)}
                </p>
              )}

              {/* Content */}
              <div
                className="news-content"
                style={{
                  fontSize: '16px',
                  lineHeight: '1.8',
                  color: '#444'
                }}
                dangerouslySetInnerHTML={{ __html: newsItem.content }}
              />

              {/* Action Buttons */}
              <div style={{ marginTop: '40px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Link to="/news" style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  backgroundColor: '#e74c3c',
                  color: '#fff',
                  textDecoration: 'none',
                  borderRadius: '5px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.3s ease'
                }}>
                  <i className="ri-arrow-left-line"></i> Back to News
                </Link>
                {newsItem.certificate && (
                  <a
                    href={newsItem.certificate}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 24px',
                      backgroundColor: '#065f46',
                      color: '#fff',
                      textDecoration: 'none',
                      borderRadius: '5px',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    <i className="ri-file-pdf-2-line"></i> View Certificate
                  </a>
                )}
              </div>
            </div>

            {/* Sidebar - Related News */}
            {relatedNews.length > 0 && (
              <div style={{ flex: '1', minWidth: '280px' }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '20px',
                  paddingBottom: '10px',
                  borderBottom: '3px solid #e74c3c'
                }}>Related News</h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {relatedNews.map((news) => (
                    <Link
                      to={`/news/${news.id}`}
                      key={news.id}
                      style={{
                        display: 'flex',
                        gap: '15px',
                        textDecoration: 'none',
                        padding: '10px',
                        borderRadius: '8px',
                        transition: 'background-color 0.3s ease'
                      }}
                      className="related-news-item"
                    >
                      <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        flexShrink: 0
                      }}>
                        <img
                          src={news.image || defaultImage}
                          alt={news.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.src = defaultImage;
                          }}
                        />
                      </div>
                      <div>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#333',
                          marginBottom: '5px',
                          lineHeight: '1.4',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>{news.title}</h4>
                        {news.date && (
                          <p style={{
                            fontSize: '12px',
                            color: '#999'
                          }}>{formatDate(news.date)}</p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .news-content p {
          margin-bottom: 16px;
        }
        .news-content ul, .news-content ol {
          margin: 16px 0;
          padding-left: 24px;
        }
        .news-content li {
          margin-bottom: 8px;
        }
        .related-news-item:hover {
          background-color: #f8f9fa;
        }
        @media (max-width: 768px) {
          .tb-page-content > div {
            flex-direction: column !important;
          }
        }
      `}</style>
    </section>
  );
};

export default ThrowballNewsDetail;
